import Vue from 'vue';
import VueI18Next from '@panter/vue-i18next';
import i18next from 'i18next';

import { get } from 'lodash';
import './switches';
import UScopeViewer from './_uScope';
import { PluginService } from './lib/services/plugin-service';
import { I18nService } from 'shared/services/I18nService';
import { EventChannel } from 'lbs-pi-handy/src/general-purpose/EventChannel';
import { create, createUScopeConfigEditor } from './uScope-config';
import { createLogger } from 'lbs-pi-handy/src/general-purpose/logger';

/**
 * Creates a class responsible for configuring and mounting uScope instance. This API is the preferred method for
 * launching uScope.
 */
export class UScopeLauncher {
  /**
   * Enables uScope to talk directly to multisite and dmz image servers
   * @param {Array} servers
   */
  configureImageServers (servers) { /* added during construction */ }

  /**
   * Set default options when an image is opened
   * @param {'Fit'|'Scan'|Number} mag - Sets the initial magnification to open an image at.
   */
  setImageDefaults ({mag}) { /* added during construction */ }

  /**
   * Sets the renderer to be used. Defaults to WebGlRenderer
   * @param Renderer
   */
  setRenderer (Renderer) { /* added during construction */ }

  /**
   * Adds an image to open when uScope launches. Note uScope supports opening up to 4 images.
   * @param {String, Number, ({imageId, compressedFileLocation})} image
   */
  addImage (image) { /* added during construction */ }

  /**
   * This is the root uScope event channel.
   * @type {EventChannel}
   */
  static events = new EventChannel(createLogger('us-global'));

  /**
   * The plugin service allows uScope to be extended with new features.
   * @type {PluginService}
   */
  static plugins = new PluginService(UScopeLauncher.events);

  /**
   * Instantiates a uScope launcher.
   * @param {UserVm} [userVm] - Object containing user credentials
   * @param {AuthVm} [authVm] - Object containing auth token
   */
  constructor (userVm, authVm) {
    this.config = create(userVm);
    Object.assign(this, createUScopeConfigEditor(this.config, authVm));
  }

  /**
   * Attaches uScope to the specified dom node. NOTE: the selector must be an ID
   * @param {String} [domId] - node to attach uScope to
   * @returns {UScopeViewer}
   */
  mount (domId) {
    if (domId) {
      if (domId.startsWith('.')) {
        throw new Error('uScope currently only supports mounting to elements with IDs');
      }
      // If user passed in css selector strip it for now
      domId = domId.replace('#', '');
      this.config.setup.divId = domId;
    }

    // TODO: Refactor to use UScopeViewer constructor.
    return UScopeLauncher.createUScope(this.config);
  }

  /**
   * This should not be necessary; uScope should have no UI components that require internaltionalizing as it's raison
   * d'etre is to display images! Required by the toolbar. TODO: Make the toolbar a plugin.
   * @returns {Promise<TFunction>|Promise<void>}
   */
  static i18nInit () {
    // If everything checks out, just skip
    if (I18nService.isInitialized) {
      return Promise.resolve();
    }
    // First ensure our own service is registered
    Vue.use(I18nService, { VueI18Next, i18next });
    // Now check to see if i18next is initialized
    if (i18next.isInitialized) {
      return Promise.resolve();
    }
    // ...and initialize it if needed
    return i18next
      .init({
        lng: 'en-US',
        ns: ['webviewer'],
        defaultNS: 'webviewer'
      });
  }

  /**
   * This factory method creates and starts a uScope instance. This is provided for convenience internally to this
   * library.
   * @deprecated The preferred approach for launching uScope is to use the UScopeLauncher constructor as it simplifies
   * configuration.
   */
  static createUScope (settings, services = {}) {
    if (!I18nService.isInitialized) {
      throw new Error('i18next is not initialized. Initialize it before starting uScope by calling `await UScopeLauncher.i18nInit()` or initializing i18next yourself.');
    }

    const images = get(settings, 'images', []);

    const uScope = new UScopeViewer(UScopeLauncher, services);

    uScope.init(settings);

    UScopeLauncher.events.trigger('lbs.uscope:start', uScope);

    if (images.length > 0) {
      uScope.imageUpdate('add', settings.images);
    }

    return uScope;
  }
}

export default UScopeLauncher;
